/** Шторка снизу. Для действия — сделал и вернулся; для чтения есть экран.
 *
 * Закрыть можно тремя способами, и это не избыточность: по фону — мышью, по
 * Escape — с клавиатуры, кнопкой — голосом и пальцем на телефоне со
 * скринридером, где ни фон нащупать, ни Escape нажать нельзя.
 *
 * Фокус уводится внутрь при открытии и возвращается на место при закрытии:
 * иначе после закрытия он падает в body, и клавиатурная навигация начинается
 * заново с начала документа.
 */
import { useEffect, useRef } from "react";

export function Sheet({ title, onClose, children }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  // Запоминаем открывшую кнопку НА РЕНДЕРЕ, а не в эффекте. `autoFocus` у поля
  // внутри шита срабатывает в фазе коммита — раньше эффектов, — и в эффекте
  // `activeElement` это уже само поле. Оно размонтируется вместе с шитом, и
  // возврат фокуса молча не происходил: фокус падал в body, ровно то, что
  // шапка этого файла обещает не допускать.
  if (opener.current === null) opener.current = document.activeElement;

  useEffect(() => {
    panel.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !panel.current) return;
      // Держим Tab внутри: иначе он уходит на фон под затемнением, где фокус
      // физически не виден.
      const stops = panel.current.querySelectorAll<HTMLElement>(
        'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
      );
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // Кнопка-источник могла размонтироваться вместе с перерисовкой списка —
      // тогда возвращать некуда, и это нормально.
      const back = opener.current;
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "var(--scrim)",
               display: "flex", alignItems: "flex-end", zIndex: 10,
               animation: "scrim-in var(--t-screen) var(--ease)" }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        // Потолок считается от свободной высоты, а не от всего экрана: иначе
        // самая высокая шторка в полный экран упирается заголовком в «Закрыть».
        style={{ width: "100%", maxHeight: "calc(92% - var(--top-chrome))", overflowY: "auto",
                 animation: "sheet-in var(--t-sheet) var(--ease)",
                 background: "var(--bg)", borderRadius: "var(--r-sheet) var(--r-sheet) 0 0",
                 padding: "10px 16px calc(24px + var(--tg-safe-bottom, 0px))",
                  }}
      >
        {/* Ползунок стоит НАД строкой заголовка, а не в ней. Был `absolute`, но
            у панели нет `relative`, и он позиционировался от затемнения во весь
            экран: по горизонтали вставал по центру окна, а по вертикали падал на
            свою статическую позицию — ровно на строку с заголовком, и рисовался
            серой чертой поперёк текста во ВСЕХ шторках приложения. */}
        <div style={{ width: 38, height: 4, borderRadius: 2, background: "var(--surface-3)",
                      margin: "0 auto 4px" }} />
        <div style={{ display: "flex", alignItems: "center", gap: "var(--s-3)",
                      marginBottom: "var(--s-4)" }}>
          <h2 style={{ fontSize: "var(--f-t2)", fontWeight: 700, letterSpacing: "-0.3px",
                       margin: "12px 0 0", flexGrow: 1, textAlign: "center",
                       paddingLeft: 44 }}>
            {title}
          </h2>
          <button type="button" onClick={onClose} aria-label="Закрыть"
                  style={{ width: 44, height: 44, marginTop: 8, marginRight: -11,
                           background: "none", border: "none", cursor: "pointer",
                           display: "flex", alignItems: "center", justifyContent: "center",
                           flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)"
                 strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
